'use client';
import React from 'react';
import Link from 'next/link';
import { PaginationInfo } from '@/types/pagination';

// Iconos SVG simples para evitar dependencias externas
const ChevronLeftIcon = () => (
  <svg data-component-id="src/components/ui/Pagination.tsx:8:2" data-component-path="src/components/ui/Pagination.tsx" data-component-line="8" data-component-file="Pagination.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%20text-gray-600%22%7D" className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path data-component-id="src/components/ui/Pagination.tsx:9:4" data-component-path="src/components/ui/Pagination.tsx" data-component-line="9" data-component-file="Pagination.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg data-component-id="src/components/ui/Pagination.tsx:14:2" data-component-path="src/components/ui/Pagination.tsx" data-component-line="14" data-component-file="Pagination.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%20text-gray-600%22%7D" className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path data-component-id="src/components/ui/Pagination.tsx:15:4" data-component-path="src/components/ui/Pagination.tsx" data-component-line="15" data-component-file="Pagination.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

interface PaginationProps {
  paginationInfo: PaginationInfo;
  handlePageChange: (page: number) => void;
  className?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  paginationInfo,
  handlePageChange,
  className = ''
}) => {

  const { currentPage, totalPages, baseUrl, hasFilters } = paginationInfo;

  // Generar array de páginas a mostrar optimizado para muchas páginas
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7; // Aumentado para mejor navegación
    
    if (totalPages <= maxVisiblePages) {
      // Mostrar todas las páginas si hay 7 o menos
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Lógica optimizada para muchas páginas
      if (currentPage <= 4) {
        // Estamos cerca del inicio: mostrar primeras páginas + última
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        // Estamos cerca del final: mostrar primera + últimas páginas
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Estamos en el medio: mostrar primera, páginas alrededor de actual, última
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const pageNumbers = getPageNumbers();

  if (totalPages <= 1) {
    return null; // No mostrar paginación si solo hay una página
  }

  // Función para renderizar botón de página
  const renderPageButton = (page: string | number, isActive: boolean = false) => {
    if (typeof page === 'string') {
      // Es un separador "..."
      return (
        <span data-component-id="src/components/ui/Pagination.tsx:85:8" data-component-path="src/components/ui/Pagination.tsx" data-component-line="85" data-component-file="Pagination.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20w-10%20h-10%20text-gray-500%22%7D" key={`separator-${page}`} className="flex items-center justify-center w-10 h-10 text-gray-500">
          {page}
        </span>
      );
    }

    // Es un número de página
    if (hasFilters) {
      // Con filtros: usar onClick
      return (
        <button data-component-id="src/components/ui/Pagination.tsx:95:8" data-component-path="src/components/ui/Pagination.tsx" data-component-line="95" data-component-file="Pagination.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
          key={page}
          onClick={() => handlePageChange(page)}
          className={`flex items-center justify-center w-10 h-10 rounded-lg border transition-colors ${
            isActive
              ? 'bg-global-1 border-[#3D1C1A] text-white'
              : 'border-[#3D1C1A] text-gray-700 hover:bg-gray-50'
          }`}
        >
          <span data-component-id="src/components/ui/Pagination.tsx:104:10" data-component-path="src/components/ui/Pagination.tsx" data-component-line="104" data-component-file="Pagination.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-medium%22%7D" className="text-sm font-medium">{page}</span>
        </button>
      );
    } else {
      // Sin filtros: usar Link
      return (
        <Link data-component-id="src/components/ui/Pagination.tsx:110:8" data-component-path="src/components/ui/Pagination.tsx" data-component-line="110" data-component-file="Pagination.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D"
          key={page}
          href={`${baseUrl}${page}`}
          className={`flex items-center justify-center w-10 h-10 rounded-lg border transition-colors ${
            isActive
              ? 'bg-global-1 border-[#3D1C1A] text-white'
              : 'border-[#3D1C1A] text-gray-700 hover:bg-gray-50'
          }`}
        >
          <span data-component-id="src/components/ui/Pagination.tsx:119:10" data-component-path="src/components/ui/Pagination.tsx" data-component-line="119" data-component-file="Pagination.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-medium%22%7D" className="text-sm font-medium">{page}</span>
        </Link>
      );
    }
  };

  // Función para renderizar botón de navegación
  const renderNavButton = (direction: 'prev' | 'next', page: number) => {
    if (hasFilters) {
      return (
        <button data-component-id="src/components/ui/Pagination.tsx:129:8" data-component-path="src/components/ui/Pagination.tsx" data-component-line="129" data-component-file="Pagination.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20w-10%20h-10%20rounded-lg%20border%20border-%5B%233D1C1A%5D%20hover%3Abg-gray-50%20transition-colors%22%7D"
          onClick={() => handlePageChange(page)}
          className="flex items-center justify-center w-10 h-10 rounded-lg border border-[#3D1C1A] hover:bg-gray-50 transition-colors"
        >
          {direction === 'prev' ? <ChevronLeftIcon data-component-id="src/components/ui/Pagination.tsx:133:34" data-component-path="src/components/ui/Pagination.tsx" data-component-line="133" data-component-file="Pagination.tsx" data-component-name="ChevronLeftIcon" data-component-content="%7B%22elementName%22%3A%22ChevronLeftIcon%22%7D" /> : <ChevronRightIcon data-component-id="src/components/ui/Pagination.tsx:133:56" data-component-path="src/components/ui/Pagination.tsx" data-component-line="133" data-component-file="Pagination.tsx" data-component-name="ChevronRightIcon" data-component-content="%7B%22elementName%22%3A%22ChevronRightIcon%22%7D" />}
        </button>
      );
    } else {
      return (
        <Link data-component-id="src/components/ui/Pagination.tsx:138:8" data-component-path="src/components/ui/Pagination.tsx" data-component-line="138" data-component-file="Pagination.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20w-10%20h-10%20rounded-lg%20border%20border-%5B%233D1C1A%5D%20hover%3Abg-gray-50%20transition-colors%22%7D"
          href={`${baseUrl}${page}`}
          className="flex items-center justify-center w-10 h-10 rounded-lg border border-[#3D1C1A] hover:bg-gray-50 transition-colors"
        >
          {direction === 'prev' ? <ChevronLeftIcon data-component-id="src/components/ui/Pagination.tsx:142:34" data-component-path="src/components/ui/Pagination.tsx" data-component-line="142" data-component-file="Pagination.tsx" data-component-name="ChevronLeftIcon" data-component-content="%7B%22elementName%22%3A%22ChevronLeftIcon%22%7D" /> : <ChevronRightIcon data-component-id="src/components/ui/Pagination.tsx:142:56" data-component-path="src/components/ui/Pagination.tsx" data-component-line="142" data-component-file="Pagination.tsx" data-component-name="ChevronRightIcon" data-component-content="%7B%22elementName%22%3A%22ChevronRightIcon%22%7D" />}
        </Link>
      );
    }
  };

  return (
    <div data-component-id="src/components/ui/Pagination.tsx:149:4" data-component-path="src/components/ui/Pagination.tsx" data-component-line="149" data-component-file="Pagination.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`flex items-center justify-center space-x-2 ${className}`}>
      {/* Botón Anterior */}
      {currentPage > 1 && renderNavButton('prev', currentPage - 1)}

      {/* Páginas numeradas */}
      {pageNumbers.map((page) => renderPageButton(page, page === currentPage))}

      {/* Botón Siguiente */}
      {currentPage < totalPages && renderNavButton('next', currentPage + 1)}
    </div>
  );
};

export default Pagination; 